import React from 'react';
import { Card, Typography, Box, Chip, Stack } from '@mui/material';

import {
    LinkedIn as LinkedInIcon,
    YouTube as YouTubeIcon,
    Language as WebsiteIcon,
    OpenInNew as ExternalLinkIcon,
    Warning as WarningIcon,
    CheckCircle as CheckIcon,
    Check as CheckmarkIcon,
    Add as AddIcon,
    RemoveCircle as RemoveCircleIcon
} from '@mui/icons-material';
import { Paper } from '@mui/material';

import { Home } from '@mui/icons-material';
import { typography, colors } from '../../styles/globals';
import _ from 'lodash';
import SourceButton from '../PhoneTab/SourceButton.js';
import { formatAddress } from '../../utils/nameFormatters.js';


const StatusItem = ({ type, text, textStyle }) => {
    const isWarning = type === 'warning';
    const isSuccess = type === 'success';
    const isNeutral = type === 'neutral';

    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
            {isWarning && (
                <Box sx={{
                    backgroundColor: '#EF4444',
                    borderRadius: '50%',
                    width: 20,
                    height: 20,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0
                }}>
                    <Typography sx={{ color: 'white', fontSize: '14px', fontWeight: 'bold' }}>!</Typography>
                </Box>
            )}
            {isSuccess && (
                <Box sx={{
                    backgroundColor: '#10B981',
                    borderRadius: '50%',
                    width: 20,
                    height: 20,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0
                }}>
                    <AddIcon sx={{ fontSize: 14, color: 'white', fontWeight: 'bold' }} />
                </Box>
            )}
            {isNeutral && (
                <RemoveCircleIcon sx={{ fontSize: 20, color: '#9CA3AF' }} />
            )}
            <Typography variant="body2" sx={{
                color: '#374151',
                fontSize: '0.875rem',
                fontWeight: 400,
                ...textStyle
            }}>
                {text}
            </Typography>
        </Box>
    );
};

const AddressCard = ({ address, isSelected, onSelect }) => {
    const getRiskInfo = (riskLevel) => {
        switch (riskLevel) {
            case 'moderate':
                return {
                    text: 'Moderate risk',
                    color: '#e65100',
                    backgroundColor: '#fff3e0'
                };
            case 'high':
                return {
                    text: 'High risk',
                    color: colors.error,
                    backgroundColor: '#ffebee'
                };
            case 'low':
                return {
                    text: 'Low risk',
                    color: colors.success,
                    backgroundColor: '#e8f5e9'
                };
            default:
                return {
                    text: 'Unknown risk',
                    color: colors.grey,
                    backgroundColor: '#f5f5f5'
                };
        }
    };

    const riskInfo = getRiskInfo(address?.riskLevel);
    const statusItems = address?.assessments || [];
    const uniqueStatusItems = statusItems.filter((item, index, self) =>
        index === self.findIndex((t) => t.ui_string === item.ui_string)
    );

    return (
        <Paper sx={{
            borderRadius: '16px',
            border: isSelected ? '2px solid #B2B8CF' : '1.5px solid #E5E7EB',
            marginBottom: '8px',
            backgroundColor: isSelected ? '#F6F8FC' : '#ffffff',
            boxShadow: 'none',
            height: 'auto',
            cursor: 'pointer',
            '&:hover': {
                backgroundColor: '#f8f9fa'
            }
        }}
            onClick={onSelect}>
            <Box sx={{
                padding: '24px',
                display: 'flex',
                flexDirection: 'column',
                height: '100%'
            }}>
                {/* Top Section */}
                <Box>
                    {/* Address and Risk Pill Row */}
                    <Box sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'flex-start',
                        marginBottom: '12px',
                        gap: '16px'
                    }}>
                        <Typography sx={{
                            fontSize: '18px',
                            fontWeight: typography.fontWeights.regular,
                            color: colors.primary,
                            lineHeight: 1.4,
                            flex: 1
                        }}>
                            {formatAddress(address?.value || address?.fullAddress)}
                        </Typography>
                        {/* <Chip
                            icon={<Warning />}
                            label={riskInfo.text}
                            sx={{
                                backgroundColor: riskInfo.backgroundColor,
                                color: riskInfo.color,
                                fontSize: '14px',
                                fontWeight: 500,
                                height: '32px',
                                borderRadius: '16px',
                                flexShrink: 0,
                                '& .MuiChip-icon': {
                                    color: riskInfo.color,
                                    fontSize: '18px'
                                }
                            }}
                        /> */}
                    </Box>


                    {/* Address Type Pills */}
                    <Box sx={{
                        display: 'flex',
                        gap: '8px',
                        marginBottom: '16px',
                        flexWrap: 'wrap',
                        minHeight: 'auto'
                    }}>
                        <Chip
                            icon={<Home />}
                            label={(() => {
                                const displayNameMap = {
                                    'SOS_REGISTERED_ADDRESS': 'Registered address',
                                    'OFFICE_ADDRESS': 'Office address',
                                    'MAILING_ADDRESS': 'Mailing address',
                                    'REGISTERED_AGENT_ADDRESS': 'Agent address',
                                    'OTHER_ADDRESS': 'Other address',
                                };

                                const priorityOrder = [
                                    'OFFICE_ADDRESS',
                                    'MAILING_ADDRESS',
                                    'REGISTERED_AGENT_ADDRESS',
                                    'SOS_REGISTERED_ADDRESS',
                                    'OTHER_ADDRESS'
                                ];

                                const specificTypes = ['OFFICE_ADDRESS', 'MAILING_ADDRESS', 'REGISTERED_AGENT_ADDRESS', 'SOS_REGISTERED_ADDRESS'];
                                const redundantTypes = ['OTHER_ADDRESS', 'OWNS'];

                                const filteredTypes = _.filter(address?.association_type, type => type !== 'INPUT_DATA');

                                if (filteredTypes.length === 0) {
                                    return 'Other address';
                                }

                                const dedupedTypes = _.uniq(filteredTypes);

                                const hasSpecific = dedupedTypes.some(t => specificTypes.includes(t));
                                const displayTypes = hasSpecific
                                    ? dedupedTypes.filter(t => !redundantTypes.includes(t))
                                    : dedupedTypes;

                                const sortedTypes = _.sortBy(displayTypes, type => {
                                    const index = priorityOrder.indexOf(type);
                                    return index === -1 ? priorityOrder.length : index;
                                });

                                const limitedTypes = _.take(sortedTypes, 4);

                                return _.join(_.map(limitedTypes, type => displayNameMap[type] || _.capitalize(_.lowerCase(type))), ', ');
                            })()}
                            sx={{
                                backgroundColor: 'transparent',
                                color: '#6b7280',
                                fontSize: '12px',
                                fontWeight: 400,
                                height: '28px',
                                borderRadius: '14px',
                                '& .MuiChip-icon': {
                                    fontSize: '16px',
                                    color: '#9ca3af'
                                }
                            }}
                        />
                        {_.includes(address?.association_type, 'INPUT_DATA') && (
                            <Chip
                                label="Input Data"
                                sx={{
                                    backgroundColor: '#e3f2fd',
                                    color: '#1565c0',
                                    fontSize: '12px',
                                    fontWeight: 500,
                                    height: '28px',
                                    borderRadius: '14px',
                                    flexShrink: 0
                                }}
                            />
                        )}
                        {address?.isPrimary && (
                            <Chip
                                label="Primary address"
                                sx={{
                                    backgroundColor: '#e3f2fd',
                                    color: '#1565c0',
                                    fontSize: '12px',
                                    fontWeight: 500,
                                    height: '28px',
                                    borderRadius: '14px'
                                }}
                            />
                        )}
                    </Box>
                </Box>

                {/* Status Items */}
                <Box sx={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 0,
                    maxHeight: '160px',
                    overflowY: 'scroll',
                }}>
                    {address?.assessments && address.assessments
                        .sort((a, b) => {
                            // Sort by type: warning first, then success, then neutral
                            const getTypeOrder = (assessment) => {
                                switch(assessment.result_sign) {
                                    case 'Positive': return 0; // warning first
                                    case 'Negative': return 1; // success second
                                    default: return 2; // neutral last
                                }
                            };
                            return getTypeOrder(a) - getTypeOrder(b);
                        })
                        .filter((item, index, self) =>
                            index === self.findIndex((t) => t.ui_string === item.ui_string)
                        )
                        .map((assessment, index) => {
                            let type;
                            switch(assessment.result_sign) {
                                case 'Negative':
                                    type = 'success';
                                    break;
                                case 'Positive':
                                    type = 'warning';
                                    break;
                                default:
                                    type = 'neutral';
                            }

                            return (
                                <StatusItem
                                    key={index}
                                    type={type}
                                    text={assessment.ui_string}
                                    textStyle={{
                                        fontSize: '0.875rem',
                                        fontWeight: 400
                                    }}
                                />
                            );
                        })}
                </Box>
                {/* Source Button */}
                <Box sx={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    alignItems: 'flex-end',
                    mt: 0
                }}>
                    {uniqueStatusItems.length > 5 && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%', ml: 8, mt: 1 }}>
                            <Typography
                                variant="body2"
                                sx={{
                                    color: '#6B7280',
                                    textAlign: 'center',
                                    mt: 1,
                                    fontSize: '0.800rem',
                                }}
                            >
                                {uniqueStatusItems.length} items ↓
                            </Typography>
                        </Box>
                    )}
                    <SourceButton sources={address?.source} />
                </Box>
            </Box>
        </Paper>
    );
};

export default AddressCard;